import { Box, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import type { PropsWithChildren, ReactElement } from 'react'
import DatePicker from 'react-datepicker'
import { isNull } from 'lodash'
import DateCustomInput from '../../../DateCustomInput'
import BulkEditModeDropdown from './BulkEditModeDropdown'
import type { CMFieldProps } from '.'
import 'react-datepicker/dist/react-datepicker.css'

const CMDateField = ({
  cmField,
  disabled,
  readOnly,
  placeholder,
  isUpload,
  form,
  children,
  labelElement,
  showBulkEditFieldModeDropdown,
  bulkEditFieldMode,
  style,
  required,
  menuPosition,
  setBulkEditFieldMode,
  onInputChange,
}: PropsWithChildren<CMFieldProps>): ReactElement => {
  const shouldShowFormField = useMemo(() => {
    if (!showBulkEditFieldModeDropdown) {
      return true
    }

    return bulkEditFieldMode?.[cmField.name] === 'set'
  }, [bulkEditFieldMode, cmField.name, showBulkEditFieldModeDropdown])
  return (
    <FormControl
      marginBottom={isUpload ? 0 : '30px'}
      isInvalid={Boolean(
        form.errors?.upload &&
          form.touched?.upload &&
          form.errors?.upload[cmField.name] &&
          form.touched?.upload[cmField.name],
      )}
      isRequired={cmField.schema.isValueRequired}
      isDisabled={disabled}
      style={style}
    >
      {labelElement ?? <FormLabel>{cmField.label}</FormLabel>}
      <Box width="100%">
        {showBulkEditFieldModeDropdown &&
          bulkEditFieldMode &&
          setBulkEditFieldMode && (
            <BulkEditModeDropdown
              bulkEditFieldMode={bulkEditFieldMode}
              setBulkEditFieldMode={setBulkEditFieldMode}
              cmField={cmField}
              disabled={readOnly || disabled}
              menuPosition={menuPosition}
              onInputChange={() => {
                onInputChange?.(cmField, form)
              }}
            />
          )}
        {shouldShowFormField && (
          <>
            <DatePicker
              disabled={disabled}
              readOnly={readOnly || disabled}
              selected={
                form?.values?.upload[cmField.name]
                  ? new Date(form?.values?.upload[cmField.name])
                  : undefined
              }
              isClearable
              customInput={React.createElement(
                React.forwardRef(DateCustomInput),
                { placeholder },
              )}
              onChange={(newDate: Date | null) => {
                if (isNull(newDate)) {
                  form.setFieldValue(`upload.['${cmField.name}']`, null)
                } else {
                  form.setFieldValue(
                    `upload.['${cmField.name}']`,
                    newDate.toISOString(),
                  )
                }
                onInputChange?.(
                  cmField,
                  form,
                  newDate ? newDate.toISOString() : undefined,
                )
              }}
              required={required}
              popperPlacement="auto"
              popperModifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'clippingParents',
                  },
                },
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top', 'bottom'],
                  },
                },
              ]}
              popperProps={{
                strategy: menuPosition === 'fixed' ? 'fixed' : 'absolute',
              }}
            />
            <FormErrorMessage>
              {form.errors?.upload && form.errors?.upload[cmField.name]}
            </FormErrorMessage>
          </>
        )}
      </Box>
      {children}
    </FormControl>
  )
}

export default CMDateField
